import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLightbulb, 
  faFileText, 
  faWandMagicSparkles,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import styles from '../../Modal/Modal.module.css';
import { useAuth } from '../../../context/Auth/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { getApiUrl } from '../../../config/api';
import ScriptDropdown from './ScriptDropdown';
import ScriptPreview from './ScriptPreview';
import PromptTypeSelector from './PromptTypeSelector';

interface ScriptData {
  id: string;
  projectId: string;
  title?: string;
  generatedScript: string;
  userSelections: {
    userIdea: string;
    attributes?: string;
  };
  scriptMetadata?: {
    scriptType?: string;
  };
  createdAt: string;
}

interface ScriptToPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onProceedWithScript: (
    scriptId: string, 
    promptTypes: string[],
    useConsistentCharacters?: boolean
  ) => void;
  onProceedWithoutScript: () => void;
}

const ScriptToPromptModal: React.FC<ScriptToPromptModalProps> = ({
  isOpen,
  onClose,
  projectId,
  onProceedWithScript,
  onProceedWithoutScript
}) => {
  const [scripts, setScripts] = useState<ScriptData[]>([]);
  const [selectedScriptId, setSelectedScriptId] = useState<string>('');
  const [selectedPromptTypes, setSelectedPromptTypes] = useState<string[]>([]);
  const [useConsistentCharacters, setUseConsistentCharacters] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'select' | 'types'>('select');
  
  const { token } = useAuth();
  const { showNotification } = useNotification();
  const { t } = useTranslation('scriptGenerator');

  // Load scripts when modal opens
  useEffect(() => {
    if (isOpen && projectId) {
      loadScripts();
    }
  }, [isOpen, projectId]);

  const loadScripts = async () => {
    if (!projectId || !token) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        getApiUrl(`/script-generator/scripts/${projectId}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      const result = await response.json();
      
      if (result.success) {
        setScripts(result.data || []);
      } else {
        throw new Error(result.message || 'Failed to load scripts');
      }
    } catch (error) {
      console.error('Error loading scripts:', error);
      showNotification({
        title: t('scriptGenerator.scriptToPromptModal.errorLoadingScripts'),
        message: t('scriptGenerator.scriptToPromptModal.errorLoadingScriptsMessage'),
        type: 'error'
      });
      setScripts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleScriptSelect = (scriptId: string) => {
    setSelectedScriptId(scriptId);
  };

  const handleContinueWithScript = () => {
    if (!selectedScriptId) {
      showNotification({
        title: t('scriptGenerator.scriptToPromptModal.selectionRequired'),
        message: t('scriptGenerator.scriptToPromptModal.selectScriptMessage'),
        type: 'error'
      });
      return;
    }
    setStep('types');
  };

  const handlePromptTypeSelection = (types: string[]) => {
    setSelectedPromptTypes(types);
  };

  const handleCharacterConsistencyChange = (enabled: boolean) => {
    setUseConsistentCharacters(enabled);
  };

  const handleFinalProceed = () => {
    if (selectedPromptTypes.length === 0) {
      showNotification({
        title: t('scriptGenerator.scriptToPromptModal.selectionRequired'),
        message: t('scriptGenerator.scriptToPromptModal.selectPromptTypeMessage'),
        type: 'error'
      });
      return;
    }
    onProceedWithScript(selectedScriptId, selectedPromptTypes, useConsistentCharacters);
  };

  const handleBackToScriptSelection = () => {
    setStep('select');
    setSelectedPromptTypes([]);
  };

  const selectedScript = scripts.find(s => s.id === selectedScriptId);

  if (!isOpen) return null;

  return (
    <div className={`${styles.modalOverlay} ${styles.open}`} onClick={onClose}>
      <div 
        className={styles.modalContent} 
        onClick={(e) => e.stopPropagation()}
        style={{ 
          height: window.innerHeight > 800 ? '650px' : '75%',
          width: window.innerWidth > 480 ? '90%' : '75%',
          marginTop: window.innerHeight > 700 ? '81px' : '5vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <button 
          className={styles.closeButton} 
          onClick={onClose} 
          aria-label="Close Modal"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>

        {step === 'select' && (
          <>
            <h2 className={styles.modalTitle}>
              <FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '8px' }} />
              {t('scriptGenerator.scriptToPromptModal.title')}
            </h2>
            
            <div className={styles.modalBody} style={{ 
              overflow: 'auto',
              height: window.innerHeight > 700 ? '550px' : 'calc(90vh - 200px)',
              flex: 1,
              paddingRight: '8px'
            }}>
              <p className={styles.prompt}>
                {t('scriptGenerator.scriptToPromptModal.description')}
              </p>

              {scripts.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <ScriptDropdown
                    scripts={scripts}
                    selectedScriptId={selectedScriptId}
                    onScriptSelect={handleScriptSelect}
                    loading={loading}
                  />
                  
                  {selectedScript && (
                    <ScriptPreview script={selectedScript} />
                  )}
                </div>
              )}

              {scripts.length === 0 && !loading && (
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  color: '#a0a0a0'
                }}>
                  <FontAwesomeIcon icon={faFileText} size="2x" />
                  <p>{t('scriptGenerator.scriptToPromptModal.noScripts')}</p>
                  <p>{t('scriptGenerator.scriptToPromptModal.createScriptFirst')}</p>
                </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              {scripts.length > 0 && (
                <button
                  className={styles.button}
                  onClick={handleContinueWithScript}
                  disabled={!selectedScriptId}
                >
                  <FontAwesomeIcon icon={faWandMagicSparkles} />
                  &nbsp;{t('scriptGenerator.scriptToPromptModal.continueWithScript')}
                </button>
              )}
              
              <button
                className={styles.buttonSecondary}
                onClick={onProceedWithoutScript}
              >
                {t('scriptGenerator.scriptToPromptModal.skipToPrompt')}
              </button>
            </div>
          </>
        )}

        {step === 'types' && selectedScript && (
          <>
            <h2 className={styles.modalTitle} style={{ width: 'inherit' }}>
              <FontAwesomeIcon icon={faWandMagicSparkles} style={{ marginRight: '8px' }} />
              {t('scriptGenerator.scriptToPromptModal.selectPromptTypes')}
            </h2>
            
            <div className={styles.modalBody} style={{ 
              overflow: 'auto',
              height: window.innerHeight > 700 ? '550px' : 'calc(90vh - 200px)',
              flex: 1,
              paddingRight: '8px'
            }}>
              <p className={styles.prompt}>
                {t('scriptGenerator.scriptToPromptModal.choosePromptTypes')}
              </p>

              <PromptTypeSelector
                script={selectedScript}
                selectedTypes={selectedPromptTypes}
                onTypeSelect={handlePromptTypeSelection}
                useConsistentCharacters={useConsistentCharacters}
                onCharacterConsistencyChange={handleCharacterConsistencyChange}
              />
            </div>

            <div className={styles.modalFooter}>
              <button
                className={styles.buttonSecondary}
                onClick={handleBackToScriptSelection}
              >
                {t('scriptGenerator.scriptToPromptModal.back')}
              </button>
              
              <button
                className={styles.button}
                onClick={handleFinalProceed}
                disabled={selectedPromptTypes.length === 0}
              >
                {t('scriptGenerator.scriptToPromptModal.title')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ScriptToPromptModal;